import { useEffect, useState} from 'react';
import { useAuthStore } from '../store/authStore';
import { useProfileStore } from '../store/profileStore';
import { useSettingsStore } from '../store/settingsStore';
import { useCurrenciesStore } from '../store/currenciesStore';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber, cleanPhoneNumber, isValidPhoneNumber } from '@flows.cash/core';
import { 
  ChevronLeft, 
  Sun, 
  Moon,
  Menu,
  Settings2,
  PieChart,
  Users,
  Tags,
  FileText,
  Repeat,
  Bell,
  Wallet2,
  Globe,
  FolderCog,
  Camera,
  Loader2,
  LogOut,
  Code
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useProfilePhoto } from '@/hooks/useProfilePhoto'
import { useQueryClient } from '@tanstack/react-query'
import { useDeveloperMode } from '@flows.cash/core';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
  exact?: boolean;
}
const dateFormatOptions = [
  { label: 'MMM d, yyyy', value: 'MMM d, yyyy' },
  { label: 'yyyy-MM-dd', value: 'yyyy-MM-dd' },
  { label: 'dd/MM/yyyy', value: 'dd/MM/yyyy' },
  { label: 'MM/dd/yyyy', value: 'MM/dd/yyyy' },
  { label: 'MMMM d, yyyy', value: 'MMMM d, yyyy' },
  { label: 'd MMMM yyyy', value: 'd MMMM yyyy' },
  { label: 'dd.MM.yyyy', value: 'dd.MM.yyyy' },
  { label: 'dd-MMM-yyyy', value: 'dd-MMM-yyyy' },
  { label: 'MMM dd, yyyy', value: 'MMM dd, yyyy' },
  { label: 'yyyy/MM/dd', value: 'yyyy/MM/dd' }
];

const timeFormatOptions = [
  { label: '24-hour (HH:mm)', value: 'HH:mm' },
  { label: '12-hour (hh:mm a)', value: 'hh:mm a' },
];

const numberFormatOptions = [
  { label: '1,234.56', value: '#,##0.00' },
  { label: '1 234,56', value: '# ##0,00' },
  { label: '1.234,56', value: '#.##0,00' },
  { label: '1234.56', value: '0.00' },
];

// Extract SettingsNavigation component
const SettingsNavigation = ({ onItemClick, navigation }: { onItemClick?: () => void; navigation: NavigationItem[] }) => {
  const location = useLocation();

  return (
    <nav className="space-y-1.5 px-2">
      {navigation.map((item) => {
        const isActive = item.exact 
          ? location.pathname === item.href
          : location.pathname.startsWith(item.href);
        const Icon = item.icon;

        return (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              "group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200",
              isActive 
                ? "bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark shadow-sm" 
                : "text-text-primary dark:text-text-dark-primary hover:bg-primary/5 dark:hover:bg-primary-dark/5 hover:text-primary dark:hover:text-primary-dark"
            )}
            onClick={onItemClick}
          >
            <Icon className={cn(
              "w-5 h-5 transition-colors",
              isActive 
                ? "text-primary dark:text-primary-dark" 
                : "text-text-tertiary dark:text-text-dark-tertiary group-hover:text-primary dark:group-hover:text-primary-dark"
            )} />
            <span className="font-medium">{item.name}</span>
            {isActive && (
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary dark:bg-primary-dark" />
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export default function Settings() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuthStore();
  const { settings: profileSettings, fetchSettings: fetchProfileSettings, updateSettings: updateProfileSettings } = useProfileStore() as {
    settings: any;
    fetchSettings: (userId: string) => Promise<void>;
    updateSettings: (userId: string, data: any) => Promise<void>;
    isLoading: boolean;
  };
  const { settings, fetchSettings, updateSettings, isLoading: settingsLoading } = useSettingsStore();
  const { fetchCurrencies } = useCurrenciesStore();
  const [formData, setFormData] = useState<{
    full_name: string;
    display_name: string;
    phone: string;
  }>({
    full_name: '',
    display_name: '',
    phone: '',
  });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const profilePhoto = useProfilePhoto();
  const { isDeveloper } = useDeveloperMode();
  
  const settingsNavigation: NavigationItem[] = [
    { 
      name: t('settings.general'), 
      href: '/app/settings', 
      icon: Settings2,
      exact: true
    },
    { 
      name: t('settings.categories'), 
      href: '/app/settings/categories', 
      icon: PieChart 
    },
    { 
      name: t('settings.payees'), 
      href: '/app/settings/payees', 
      icon: Users 
    },
    { 
      name: t('settings.tags'), 
      href: '/app/settings/tags', 
      icon: Tags 
    },
    { 
      name: t('settings.rules'), 
      href: '/app/settings/rules', 
      icon: FileText 
    },
    { 
      name: t('settings.recurring'), 
      href: '/app/settings/recurring', 
      icon: Repeat 
    },
    { 
      name: t('settings.notifications'), 
      href: '/app/settings/notifications', 
      icon: Bell 
    },
    { 
      name: t('settings.currency'), 
      href: '/app/settings/currency', 
      icon: Wallet2 
    },
    { 
      name: t('settings.language'), 
      href: '/app/settings/language', 
      icon: Globe 
    },
    { 
      name: t('settings.dataManagement'), 
      href: '/app/settings/data', 
      icon: FolderCog 
    },
    ...(isDeveloper ? [{ 
      name: t('settings.developerTools.title') || 'Developer Tools', 
      href: '/app/settings/developer-tools', 
      icon: Code 
    }] : [])
  ];

  // Load settings and currencies when component mounts
  useEffect(() => {
    if (user) {
      console.log('Fetching settings for user:', user.id);
      fetchProfileSettings(user.id);
      fetchSettings(user.id);
      fetchCurrencies();
    }
  }, [user]);

  // Log settings changes
  useEffect(() => {
    console.log('Settings from settingsStore:', settings);
  }, [settings]);

  // Update form data when settings change
  useEffect(() => {
    console.log('Profile settings changed in Settings component:', profileSettings);
    if (profileSettings) {
      setFormData({
        full_name: profileSettings.full_name || '',
        display_name: profileSettings.display_name || '',
        phone: formatPhoneNumber(profileSettings.phone || ''),
      });
      setPhoneError(null);
    }
  }, [profileSettings]);

  const handleInputChange = (field: string, value: string) => {
    if (field === 'phone') {
      // Format phone number as user types
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [field]: formatted
      }));
      
      // Validate phone number
      if (value && !isValidPhoneNumber(value)) {
        setPhoneError(t('settings.profile.invalidPhoneNumber'));
      } else {
        setPhoneError(null);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    // Check for phone validation error
    if (phoneError) {
      toast.error(phoneError);
      return;
    }
    
    try {
      await updateProfileSettings(user.id, {
        full_name: formData.full_name,
        display_name: formData.display_name,
        phone: cleanPhoneNumber(formData.phone) // Clean phone for storage
      });
      
      toast.success(t('settings.updateSuccess'));
    } catch (error) {
      toast.error(t('settings.updateError'));
    }
  };

  const handleSignOut = async () => {
    try {
      setSignOutLoading(true);
      
      // Use the auth store's logout method instead of directly calling supabase
      const authStore = useAuthStore.getState();
      await authStore.logout();
      
      // Clear any remaining auth states from session storage
      sessionStorage.removeItem('auth_completed');
      sessionStorage.removeItem('auth_attempt_count');
      sessionStorage.removeItem('auth_in_progress');
      
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error(t('settings.signOutError'));
    } finally {
      setSignOutLoading(false);
    }
  };

  const handlePreferenceChange = async (preference: string, value: any) => {
    if (!user?.id) return;

    console.log(`Updating preference ${preference} to ${value} for user ${user.id}`);
    
    try {
      // Use the settings store to update preferences
      await updateSettings(user.id, {
        [preference]: value
      });
      
      console.log(`Successfully updated ${preference} to ${value}`);
      toast.success(t('settings.preferenceSuccess'));
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error(t('settings.preferenceError'));
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0] || !user?.id) return
    
    setIsLoading(true)
    
    try {
      const file = event.target.files[0]
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(t('settings.photoSizeError'))
      }

      // Create a path that follows the structure expected by our storage policies
      const fileName = `profile-photo-${Date.now()}.${file.name.split('.').pop()}`
      const filePath = `${user.id}/${fileName}`
      
      // Upload file to Supabase Storage
      const { error } = await supabase.storage
        .from('profiles')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true,
          cacheControl: '3600',
        })

      if (error) {
        throw error
      }

      // Update user profile with the storage path (not the public URL)
      // This matches the mobile app's approach and allows for proper signed URL generation
      await updateProfileSettings(user.id, { 
        photo_url: filePath, // Store the path, not the full URL
        updated_at: new Date().toISOString()
      })

      // Invalidate the profile photo cache
      await queryClient.invalidateQueries({ queryKey: ['profilePhoto'] })
      
      toast.success(t('settings.photoSuccess'))
    } catch (error) {
      console.error('Error uploading photo:', error)
      const message = error instanceof Error ? error.message : t('settings.photoError')
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const isProfilePage = location.pathname === '/app/settings';

  useEffect(() => {
    if (location.pathname === '/app/settings') {
      const firstItem = settingsNavigation.find(item => item.exact);
      if (firstItem) {
        navigate(firstItem.href);
      }
    }
  }, [location.pathname, navigate]);



  return (
    <div className="min-h-screen bg-background-primary dark:bg-background-dark-primary">
      <header className="sticky top-0 z-10 bg-background-secondary dark:bg-background-dark-secondary border-b border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
        <div className="container mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="hover:bg-secondary/10 dark:hover:bg-primary-dark/10 -ml-2 h-9 w-9 rounded-lg"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                  {t('settings.title')}
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary hidden sm:block">
                  {t('settings.description')}
                </p>
              </div>
            </div>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden hover:bg-secondary/10 dark:hover:bg-primary-dark/10 h-9 w-9 rounded-lg"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="left" 
                className="w-[300px] bg-background-secondary dark:bg-background-dark-secondary border-r border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150"
              >
                <div className="py-4">
                  <SettingsNavigation navigation={settingsNavigation} onItemClick={() => setOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 rounded-2xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 p-4">
              <SettingsNavigation navigation={settingsNavigation} />
            </div>
          </div>

          {/* Main Content */}
          <main className="flex-1">
            {isProfilePage ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-2xl mx-auto"
              >
                <Card className="border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-xl">
                  <CardContent className="p-4 sm:p-6">
                    <div className="space-y-6 sm:space-y-8">
                      {/* Profile Photo Section */}
                      <div className="flex flex-col items-center text-center">
                        <div className="relative group">
                          <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-background-secondary dark:border-background-dark-secondary shadow-xl">
                            <AvatarImage 
                              src={profilePhoto.data?.url}
                              alt={profileSettings?.full_name || user?.email || 'User profile'}
                              referrerPolicy="no-referrer"
                              className={cn(
                                "transition-opacity duration-200",
                                profilePhoto.isLoading && "opacity-0"
                              )}
                              onError={(e) => {
                                console.error('Error loading avatar image:', e)
                                // Hide broken image icon
                                ;(e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            <AvatarFallback className="bg-secondary dark:bg-primary-dark text-2xl sm:text-3xl text-white">
                              {profileSettings?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          
                          <label
                            htmlFor="photo-upload"
                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
                          >
                            {isLoading ? (
                              <Loader2 className="w-6 h-6 text-white animate-spin" />
                            ) : (
                              <Camera className="w-6 h-6 text-white" />
                            )}
                          </label>
                          <input
                            type="file"
                            id="photo-upload"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={handlePhotoUpload}
                            disabled={isLoading}
                          />
                        </div>
                        <div className="mt-4 space-y-1">
                          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                            {t('settings.profile.uploadPhoto')}
                          </p>
                          <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                            {t('settings.profile.photoRequirements', { size: 5 })}
                          </p>
                        </div>
                      </div>

                      {/* Profile Information */}
                      <div className="space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="full_name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.fullName')}
                            </Label>
                            <Input
                              id="full_name"
                              value={formData.full_name}
                              onChange={(e) => handleInputChange('full_name', e.target.value)}
                              placeholder={t('settings.profile.fullNamePlaceholder')}
                              className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="display_name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.displayName')}
                            </Label>
                            <Input
                              id="display_name"
                              value={formData.display_name}
                              onChange={(e) => handleInputChange('display_name', e.target.value)}
                              placeholder={t('settings.profile.displayNamePlaceholder')}
                              className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.email')}
                            </Label>
                            <Input
                              value={user?.email || ''}
                              readOnly
                              className="bg-background-input/50 dark:bg-background-dark-input/50 border-border dark:border-border-dark cursor-not-allowed h-11 rounded-xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.mobileNumber')}
                            </Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              placeholder={t('settings.profile.mobileNumberPlaceholder')}
                              className={cn(
                                "bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl",
                                phoneError && "border-red-500 dark:border-red-400"
                              )}
                            />
                            {phoneError && (
                              <p className="text-sm text-red-500 dark:text-red-400">
                                {phoneError}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Theme Toggle */}
                        <div className="flex items-center justify-between p-4 rounded-xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                          <div className="space-y-1">
                            <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.theme')}
                            </h3>
                            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                              {t('settings.profile.themeDescription')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary" />
                            <Switch
                              checked={settings?.theme === 'dark'}
                              onCheckedChange={(checked) => {
                                if (!user?.id) return;
                                const newTheme = checked ? 'dark' : 'light';
                                handlePreferenceChange('theme', newTheme);
                              }}
                              className="data-[state=checked]:bg-secondary dark:data-[state=checked]:bg-primary-dark"
                            />
                            <Moon className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary" />
                          </div>
                        </div>

                        {/* Date and Time Settings */}
                        <div className="space-y-6 rounded-xl border border-border dark:border-border-dark p-4 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                          <div className="space-y-1">
                            <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {t('settings.profile.formatSettings')}
                            </h3>
                            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                              {t('settings.profile.formatSettingsDescription')}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col space-y-2">
                              <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary" htmlFor="dateFormat">
                                {t('settings.profile.dateFormat')}
                              </Label>
                              <Select
                                value={settings?.date_format || 'MMM d, yyyy'}
                                onValueChange={(value) => {
                                  if (user?.id) {
                                    handlePreferenceChange('date_format', value);
                                  }
                                }}
                              >
                                <SelectTrigger 
                                  id="dateFormat" 
                                  className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl"
                                >
                                  <SelectValue placeholder={t('settings.profile.dateFormatPlaceholder')} />
                                </SelectTrigger>
                                <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                                  {dateFormatOptions.map((option) => (
                                    <SelectItem 
                                      key={option.value} 
                                      value={option.value}
                                      className="hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex flex-col space-y-2">
                              <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary" htmlFor="timeFormat">
                                {t('settings.profile.timeFormat')}
                              </Label>
                              <Select
                                value={settings?.time_format || 'HH:mm'}
                                onValueChange={(value) => {
                                  if (user?.id) {
                                    handlePreferenceChange('time_format', value);
                                  }
                                }}
                              >
                                <SelectTrigger 
                                  id="timeFormat" 
                                  className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl"
                                >
                                  <SelectValue placeholder={t('settings.profile.timeFormatPlaceholder')} />
                                </SelectTrigger>
                                <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                                  {timeFormatOptions.map((option) => (
                                    <SelectItem 
                                      key={option.value} 
                                      value={option.value}
                                      className="hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex flex-col space-y-2">
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary" htmlFor="numberFormat">
                              {t('settings.profile.numberFormat')}
                            </Label>
                            <Select
                              value={settings?.number_format || '#,##0.00'}
                              onValueChange={(value) => {
                                if (user?.id) {
                                  handlePreferenceChange('number_format', value);
                                }
                              }}
                            >
                              <SelectTrigger 
                                id="numberFormat" 
                                className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark h-11 rounded-xl"
                              >
                                <SelectValue placeholder={t('settings.profile.numberFormatPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                                {numberFormatOptions.map((option) => (
                                  <SelectItem 
                                    key={option.value} 
                                    value={option.value}
                                    className="hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                        <Button
                          variant="outline"
                          onClick={handleSignOut}
                          disabled={signOutLoading}
                          className="w-full sm:w-auto border-border dark:border-border-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 h-11 rounded-xl"
                        >
                          {signOutLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <LogOut className="w-4 h-4 mr-2" />
                          )}
                          {t('dashboard.userMenu.signOut')}
                        </Button>
                        <Button
                          onClick={handleSave}
                          disabled={settingsLoading}
                          className="w-full sm:w-auto bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 shadow-lg shadow-primary/20 dark:shadow-primary-dark/20 hover:shadow-xl hover:shadow-primary/30 dark:hover:shadow-primary-dark/30 h-11 rounded-xl disabled:bg-text-secondary/20 dark:disabled:bg-text-dark-secondary/20 disabled:shadow-none"
                        >
                          {settingsLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t('common.saving')}
                            </>
                          ) : (
                            t('actions.save')
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
